import { useCallback } from 'react';
import { Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  useGetEmailSubscriptionQuery,
  useResubscribeEmailMutation,
} from '@circuitry-ai/doc-data';
import { CtyShowToast, checkType } from '@circuitry-ai/doc-ui';

function getErrorStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const candidate = error as { status?: unknown; status_code?: unknown; originalStatus?: unknown };
  const values = [candidate.status, candidate.status_code, candidate.originalStatus];
  for (const value of values) {
    if (typeof value === 'number') {
      return value;
    }
  }
  return undefined;
}

function SuppressionSource({ reason }: { reason?: string }) {
  return (
    <p className="mt-2 text-xs text-default-500">
      Source: <span className="font-medium text-default-600">Platform notifications service</span>
      {reason ? (
        <>
          {' · '}Suppression reason: <span className="font-medium text-default-600">{reason}</span>
        </>
      ) : null}
    </p>
  );
}

/**
 * Global email suppression banner shown at the top of the watchlist tab.
 * - Calls GET /email-subscription on mount.
 * - is_suppressed === true  -> amber card (Resubscribe when reason is "unsubscribe",
 *   otherwise a "contact support" note for bounce/complaint suppressions).
 * - is_suppressed === false -> green "email is active" status card.
 */
function EmailSubscriptionBanner() {
  const { data, isLoading, isError } = useGetEmailSubscriptionQuery();
  const [resubscribe, { isLoading: isResubscribing }] = useResubscribeEmailMutation();

  const status = data?.data;
  const suppressionId = status?.suppression_id;
  const reason = status?.reason;
  const canResubscribe = reason === 'unsubscribe';

  const handleResubscribe = useCallback(async () => {
    if (!suppressionId) {
      return;
    }
    try {
      const response = await resubscribe({ suppression_id: suppressionId }).unwrap();
      CtyShowToast(
        checkType.success,
        response?.data?.message ?? response?.message ?? 'Resubscribed successfully.'
      );
    } catch (error) {
      if (getErrorStatusCode(error) === 403) {
        CtyShowToast(
          checkType.error,
          'This email was suppressed due to a bounce or complaint. Please contact support.'
        );
        return;
      }
      CtyShowToast(checkType.error, 'Failed to resubscribe. Please try again.');
    }
  }, [resubscribe, suppressionId]);

  // Keep the page clean while loading or if the status check fails.
  if (isLoading || isError || !status) {
    return null;
  }

  if (status.is_suppressed) {
    return (
      <div className="rounded-xl border border-warning bg-warning/10 px-4 py-4 sm:px-5">
        <div className="flex items-start gap-3">
          <Icon
            icon="solar:danger-triangle-bold"
            className="mt-0.5 shrink-0 text-warning"
            width={20}
            height={20}
          />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-warning">
              Your email is globally unsubscribed from all Circuitry emails
            </h3>
            <p className="mt-1 text-sm text-default-600 leading-relaxed">
              You clicked &quot;Unsubscribe&quot; in an email footer. While this is active,{' '}
              <span className="font-semibold text-foreground">no notification emails will reach you</span>{' '}
              — even rules you haven&apos;t paused in your watchlist below.
            </p>
            {!canResubscribe && (
              <p className="mt-2 text-sm font-medium text-warning">
                This suppression can&apos;t be lifted here — please contact support.
              </p>
            )}
            <SuppressionSource reason={reason} />
          </div>
          {canResubscribe && (
            <Button
              size="sm"
              variant="solid"
              color="success"
              className="shrink-0 bg-success text-white hover:bg-success"
              isLoading={isResubscribing}
              onPress={handleResubscribe}
            >
              Resubscribe
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-success bg-success/10 px-4 py-4 sm:px-5">
      <div className="flex items-start gap-3">
        <Icon
          icon="solar:check-circle-bold"
          className="mt-0.5 shrink-0 text-success"
          width={20}
          height={20}
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-success">Your email is active on the platform</h3>
          <p className="mt-1 text-sm text-default-600 leading-relaxed">
            The global suppression has been lifted. Notification emails will now reach your inbox again —
            subject to your per-rule settings below.
          </p>
          <SuppressionSource reason={reason} />
        </div>
      </div>
    </div>
  );
}

export default EmailSubscriptionBanner;
